import { defineStore } from 'pinia'
import { useFirebaseApp, useFirestore } from 'vuefire';
import { StripePayments, getStripePayments } from "@invertase/firestore-stripe-payments";
import { TenantCreationRequest, Invoice, ProjectCreationRequest, ProjectCreationRequestType, PricingPlans, CheckoutSession, CheckoutSessionRequest, CheckoutSessionRequestStatus, PriceLookupKeys, MemberManagementAction, MemberManagementRequestStatus } from '@/models/Billing'
import { DocRef } from '@/models/Models'
import { Project } from '@/models/Project'
import { updateModelDoc } from './documentWrites'
import { doc, addDoc, collection, DocumentSnapshot, getDocs, getDoc, onSnapshot, serverTimestamp, Timestamp, Unsubscribe, orderBy, query, limit, where } from 'firebase/firestore';
import { useUsersStore } from './users';
import { TenantPlan, TenantCustomerPortalUpdateStatus, Tenant, TenantServiceKey, isCreditPlan } from '@/models/Tenant';
import type { CreditBalance } from '@/models/Tenant';
import type { PreferredLanguage } from '@/models/User';
import { PlanSwitchRequest, PlanSwitchRequestStatus } from '@/models/PlanSwitchRequest';
import { useTenantsStore } from './tenants';
import { useAppStore } from './app';
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import { useAnalyticsStore } from './analytics'
import { logger } from '@/utils/logger'
import { wholeCredits } from '@/utils/billing'


export const useBillingStore = defineStore('billing', {
  state: () => ({
    tenantCreationRequestsUnsub: null as Unsubscribe | null,
    tenantCreationRequestSnapshot: null as DocumentSnapshot | null,
    projectCreationRequestsUnsub: null as Unsubscribe | null,
    projectCreationRequestSnapshot: null as DocumentSnapshot | null,
    checkoutSessionsUnsub: null as Unsubscribe | null,
    checkoutSessionsSnapshot: null as DocumentSnapshot | null,
    checkoutSessionRequestsUnsub: null as Unsubscribe | null,
    checkoutSessionRequestSnapshot: null as DocumentSnapshot | null,
    // Plan switch request subscription
    planSwitchUnsub: null as Unsubscribe | null,
    planSwitchRequestSnapshot: null as DocumentSnapshot | null,
    isLoadingPlanSwitch: false,
    pricingPlans: null as PricingPlans | null,
    pricingPlansError: null as string | null,
    isLoadingPricingPlans: false,
    stripe: null as StripePayments | null,
    invoices: [] as Invoice[],
    // Credit balance for credit-plan tenants (polled, not subscribed)
    creditBalance: null as CreditBalance | null,
    creditBalanceTenantId: null as string | null,
    creditBalanceTimer: null as ReturnType<typeof setInterval> | null,
    // Credit estimates from credit_config/estimates (loaded once)
    creditEstimates: null as Record<string, number> | null,
  }),
  getters: {
    tenantCreationRequest: (state) => state.tenantCreationRequestSnapshot ? TenantCreationRequest.fromFirebase(state.tenantCreationRequestSnapshot) : null,
    projectCreationRequest: (state) => state.projectCreationRequestSnapshot ? ProjectCreationRequest.fromFirebase(state.projectCreationRequestSnapshot) : null,
    checkoutSession: (state) => state.checkoutSessionsSnapshot ? CheckoutSession.fromFirebase(state.checkoutSessionsSnapshot) : null,
    checkoutSessionRequest: (state) => state.checkoutSessionRequestSnapshot ? CheckoutSessionRequest.fromFirebase(state.checkoutSessionRequestSnapshot) : null,
    planSwitchRequest: (state) => state.planSwitchRequestSnapshot ? PlanSwitchRequest.fromFirebase(state.planSwitchRequestSnapshot) : null,
    planSwitchStatus(): PlanSwitchRequestStatus | null {
      const request = this.planSwitchRequest
      if (!request) return null
      return request.status
    },
    // The whole credits the billing page shows as "Available" (MYAC-1602):
    // recurring and top-up each rounded toward zero, then summed — the same
    // number as the two tiles beside it. Every credit gate and banner reads
    // this getter, so what the page shows and what the gates allow can never
    // disagree once balances are fractional (MYAC-885): 0.6 + 0.6 shows 0 and
    // blocks, rather than showing 0 and letting the action through.
    totalCredits(): number {
      if (!this.creditBalance) return 0
      return wholeCredits(this.creditBalance.credits_recurring) + wholeCredits(this.creditBalance.credits_topup)
    },
    isLowBalance(): boolean {
      if (!this.creditBalance) return false
      const allowance = this.creditBalance.credits_recurring_allowance
      if (allowance <= 0) return false
      return this.totalCredits < allowance * 0.1
    },
    hasSufficientCredits() {
      return (action: string, count?: number): boolean => {
        if (!this.creditBalance || !this.creditEstimates) return true
        const perUnit = this.creditEstimates[action] ?? 0
        const estimate = count !== undefined ? perUnit * count : perUnit
        return this.totalCredits >= estimate
      }
    },
    // Flat ≥1-credit threshold (Aymeric, 2026-08-17) — the CreditGate policy
    // for credit-plan tenants, independent of any per-action estimate. See
    // `useCreditGateBlocked` (front/src/composables). Mirrors
    // `hasSufficientCredits`'s (and `features.ts`'s `isCreditsBlocked`)
    // null-guard: `creditBalance` is null until `fetchCreditBalance`
    // resolves, and that "not yet loaded" window must never read as
    // blocked — `totalCredits` returns 0 for null balance, which would
    // otherwise misreport every credit-plan tenant as blocked on mount.
    hasAtLeastOneCredit(): boolean {
      if (!this.creditBalance) return true
      return this.totalCredits >= 1
    },
    regulationWatchCostPerNorm(): number {
      return this.creditEstimates?.regulation_watch_per_norm ?? 0
    },
    /**
     * MYAC-776 — the per-CHANGED-CLAUSE price of a gap analysis
     * (`credit_config/estimates.gap_analysis_per_clause`).
     *
     * D22 reverses the original flat `gap_analysis_run`: the estimate follows
     * the number of clauses the redline changed, deliberately slightly low —
     * "this is just a security so big actions don't set too much negative
     * credits, not the actual cost".
     *
     * `null`, never `0`, when the key is absent (D17). The confirm step renders
     * 0 as "this run does not use any credit", so the old `?? 0` showed a
     * MISSING price to the client as free and let the run start. A price we do
     * not know fails closed; only an explicit zero may read as free.
     */
    gapAnalysisPerClause(): number | null {
      const value = this.creditEstimates?.gap_analysis_per_clause
      return typeof value === 'number' ? value : null
    },
  },
  actions: {
    initStripe: async function (): Promise<void> {
      if (this.stripe) return;
      const app = await useFirebaseApp();
      const config = {
        productsCollection: "stripe_products",
        customersCollection: "stripe_customers",
      };
      this.stripe = await getStripePayments(app, config);
    },
    // ---------------- Plan Switch Requests ----------------
    stopPlanSwitchSubscription: function (): void {
      if (this.planSwitchUnsub) {
        this.planSwitchUnsub()
        this.planSwitchUnsub = null
      }
      this.planSwitchRequestSnapshot = null
      this.isLoadingPlanSwitch = false
    },

    // ---------------- Credit Balance (polled every 60s) ----------------
    async fetchCreditBalance(tenantId: string): Promise<void> {
      try {
        const db = useFirestore()
        const ref = doc(db, 'tenants', tenantId, 'credit_balance', 'current')
        const snap = await getDoc(ref)
        if (snap.exists()) {
          const data = snap.data()
          this.creditBalance = {
            credits_recurring: data.credits_recurring ?? 0,
            credits_topup: data.credits_topup ?? 0,
            credits_recurring_allowance: data.credits_recurring_allowance ?? 0,
            last_reset_at: data.last_reset_at?.toDate?.() ?? null,
          }
        } else {
          this.creditBalance = null
        }
      } catch (error) {
        logger.warn('Failed to fetch credit balance', error)
      }
    },

    async fetchCreditEstimates(): Promise<void> {
      if (this.creditEstimates) return
      try {
        const db = useFirestore()
        const ref = doc(db, 'credit_config', 'estimates')
        const snap = await getDoc(ref)
        if (snap.exists()) {
          this.creditEstimates = snap.data() as Record<string, number>
        }
      } catch (error) {
        logger.warn('Failed to fetch credit estimates', error)
      }
    },

    startCreditBalancePolling(tenantId: string): void {
      // Skip if already polling for this tenant
      if (this.creditBalanceTenantId === tenantId && this.creditBalanceTimer) return

      this.stopCreditBalancePolling()
      this.creditBalanceTenantId = tenantId

      // Fetch immediately, then poll every 60s
      this.fetchCreditBalance(tenantId)
      this.fetchCreditEstimates()
      this.creditBalanceTimer = setInterval(() => {
        this.fetchCreditBalance(tenantId)
      }, 60_000)
    },

    stopCreditBalancePolling(): void {
      if (this.creditBalanceTimer) {
        clearInterval(this.creditBalanceTimer)
        this.creditBalanceTimer = null
      }
      this.creditBalanceTenantId = null
      this.creditBalance = null
    },

    handlePlanSwitchSnapshot: function (snap: DocumentSnapshot): void {
      this.planSwitchRequestSnapshot = snap
      try {
        const request = PlanSwitchRequest.fromFirebase(snap)
        if (!request) {
          return
        }

        if (request.status === PlanSwitchRequestStatus.FAILED) {
          try {
            useAppStore().toast('Billing', request.error || 'Plan switch failed', 'error', 5000)
          } catch {
            // ignore toast errors
          }
          this.stopPlanSwitchSubscription()
          return
        }

        if (request.status === PlanSwitchRequestStatus.DONE) {
          // Priority 1: Direct payment URL (Stripe hosted invoice)
          if (request.payment_url) {
            window.location.replace(request.payment_url)
            this.stopPlanSwitchSubscription()
            return
          }

          // Priority 2: Checkout session request (existing flow)
          if (request.checkout_session_request) {
            try {
              this.startCheckoutFromRequest(request.checkout_session_request)
            } catch (e) {
              logger.error('Error starting checkout from plan switch request', e)
            } finally {
              this.stopPlanSwitchSubscription()
            }
            return
          }

          // Priority 3: No payment needed (e.g., downgrade with credit)
          logger.info('Plan switch completed without payment')
          this.stopPlanSwitchSubscription()
          return
        }

        // requested / pending: keep listening
      } catch (e) {
        logger.error('Error handling plan switch request snapshot', e)
      }
    },

    subscribeToPlanSwitchRequest: async function (tenantId: string, requestId: string): Promise<void> {
      this.stopPlanSwitchSubscription()
      const db = useFirestore()
      const ref = doc(db, 'tenants', tenantId, 'plan_switch_requests', requestId)
      this.planSwitchUnsub = onSnapshot(ref, (snap) => {
        this.handlePlanSwitchSnapshot(snap)
      })
    },

    startPlanSwitchFlow: async function (tenantId: string, targetPlan: TenantPlan, targetPriceLookupKey?: string): Promise<void> {
      try {
        this.stopPlanSwitchSubscription()
        this.isLoadingPlanSwitch = true
        const db = useFirestore()
        const colRef = collection(db, `tenants/${tenantId}/plan_switch_requests`)
        const payload: Record<string, unknown> = {
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          status: PlanSwitchRequestStatus.REQUESTED,
          target_plan: targetPlan,
        }
        if (targetPriceLookupKey) {
          payload.target_price_lookup_key = targetPriceLookupKey
        }
        const docRefCreated = await addDoc(colRef, payload)

        await this.subscribeToPlanSwitchRequest(tenantId, docRefCreated.id)
        // The INTENT is the signal — a win-back that stalls at checkout is exactly
        // the case CSM wants to see, so this fires once the request write (the call
        // that can refuse) has actually landed, not before. Bucketed (not
        // projectMeta): this event is tenant-scoped, no project, so projectMeta
        // would be silently dropped by recordActivity.
        try {
          trackAction(CsmAction.BillingPlanSwitchStarted, { bucket: targetPlan })
        } catch { }
      } catch (error) {
        logger.error('Error creating plan switch request', error)
        try {
          useAppStore().toast('Billing', 'Error creating plan switch request', 'error', 5000)
        } catch {
          // ignore toast errors
        }
        this.isLoadingPlanSwitch = false
      }
    },
    startCheckoutFromRequest: async function (requestRef: DocRef): Promise<void> {
      if (this.checkoutSessionRequestsUnsub) {
        this.checkoutSessionRequestsUnsub()
        this.checkoutSessionRequestsUnsub = null
      }
      const db = useFirestore()
      const ref = requestRef?.toDocumentReference(db)
      if (!ref) {
        try { useAppStore().toast('Billing', 'Invalid checkout request reference', 'error', 4000) } catch { }
        return
      }
      this.checkoutSessionRequestsUnsub = onSnapshot(ref, (s) => {
        this.checkoutSessionRequestSnapshot = s
        try {
          const req = CheckoutSessionRequest.fromFirebase(s)
          const checkoutSessionRef = req?.checkout_session?.toDocumentReference(db) || null
          if (checkoutSessionRef) {
            if (this.checkoutSessionsUnsub) {
              this.checkoutSessionsUnsub()
              this.checkoutSessionsUnsub = null
            }
            this.checkoutSessionsUnsub = onSnapshot(checkoutSessionRef, (cs) => {
              this.checkoutSessionsSnapshot = cs
            })
          } else {
            if (this.checkoutSessionsUnsub) {
              this.checkoutSessionsUnsub()
              this.checkoutSessionsUnsub = null
            }
            this.checkoutSessionsSnapshot = null
          }
        } catch (e) {
          logger.error('Error handling checkout request subscription', e)
        }
      })
    },
    buildInitialQuantitiesForTenant: async function (tenant: Tenant, priceLookupKey?: string): Promise<Record<string, number>> {
      await this.loadActivePricingPlans()
      const plans = this.pricingPlans
      if (!plans) throw new Error('No pricing plans found')
      if (!tenant?.plan) throw new Error('No tenant plan found')
      const plan = plans.getByPlan(tenant.plan)
      if (!plan) throw new Error(`No plan found for tenant plan ${tenant.plan}`)

      const quantities: Record<string, number> = {}

      // Credit-based plans: single item, quantity=1
      if (isCreditPlan(tenant.plan)) {
        let pricing = priceLookupKey ? plan.getByPriceLookupKey(priceLookupKey) : null
        if (!pricing) pricing = plan.getSubscriptionPricing()
        if (!pricing) throw new Error('No subscription pricing found for credit plan')
        if (!pricing.price_id) throw new Error('No subscription price id found for credit plan')
        quantities[pricing.price_id] = 1
        return quantities
      }

      switch (tenant.plan) {
        case TenantPlan.FREE_TRY: {
          const freeTry = plan.getByQuotaKey(TenantServiceKey.QUOTA_USERS)
          if (!freeTry) throw new Error('No free try found')
          if (!freeTry.price_id) throw new Error('No free try price id found')
          quantities[freeTry.price_id] = 1
          break
        }
        case TenantPlan.FREE: {
          const free = plan.getByQuotaKey(TenantServiceKey.QUOTA_USERS)
          if (!free) throw new Error('No free found')
          if (!free.price_id) throw new Error('No free price id found')
          quantities[free.price_id] = 1
          const single = plan.getByPriceLookupKey(PriceLookupKeys.SINGLE_ASSESSMENT)
          if (!single) throw new Error('No single assessment found')
          if (!single.price_id) throw new Error('No single assessment price id found')
          quantities[single.price_id] = 1
          break
        }
        case TenantPlan.EARLY_ACCESS:
        case TenantPlan.EVALUATOR:
        case TenantPlan.STUDENT: {
          const users = plan.getByQuotaKey(TenantServiceKey.QUOTA_USERS)
          if (!users) throw new Error('No users pricing found')
          if (!users.price_id) throw new Error('No users price id found')
          const usersSvc = tenant?.services?.quota_users
          let quantity = ((usersSvc?.quota || 0) - (usersSvc?.quota_remaining || 0))
          if (quantity > 0) {
            quantities[users.price_id] = quantity
          }

          const qms = plan.getByQuotaKey(TenantServiceKey.QUOTA_USERS_QMS)
          if (!qms) throw new Error('No QMS pricing found')
          if (!qms.price_id) throw new Error('No QMS price id found')
          const qmsSvc = tenant?.services?.quota_users_qms
          quantity = ((qmsSvc?.quota || 0) - (qmsSvc?.quota_remaining || 0))
          if (quantity > 0) {
            quantities[qms.price_id] = quantity
          }

          // For evaluator, early access, and student plans, ensure at least one QMS seat is always included
          if (tenant.plan === TenantPlan.EVALUATOR || tenant.plan === TenantPlan.EARLY_ACCESS || tenant.plan === TenantPlan.STUDENT) {
            quantities[qms.price_id] = Math.max(quantities[qms.price_id] || 0, 1)
          }

          // Fallback: ensure we send at least one seat if no item computed,
          // so backend gets non-empty subscription_data[items]
          if (Object.keys(quantities).length === 0) {
            quantities[users.price_id] = 1
            // Always include QMS seat for evaluator, early access, and student plans
            if ((tenant.plan === TenantPlan.EVALUATOR || tenant.plan === TenantPlan.EARLY_ACCESS || tenant.plan === TenantPlan.STUDENT) && qms?.price_id) {
              quantities[qms.price_id] = 1
            }
          }
          break
        }
        default:
          break
      }
      return quantities
    },
    createCheckoutSession: async function (tenantId: string, priceLookupKey?: string): Promise<void> {
      const tenantsStore = useTenantsStore()
      // Ensure subscription is active (will skip if already subscribed)
      await tenantsStore.listTenants(false)
      const current = tenantsStore.currentTenant
      if (!current?.id) throw new Error('No tenant found')
      if (current.id !== tenantId) throw new Error(`Tenant ID mismatch: expected ${tenantId}, got ${current.id}`)
      if (!current.plan) throw new Error('No tenant plan found')
      const plan = current.plan as TenantPlan
      const quantities = await this.buildInitialQuantitiesForTenant(current, priceLookupKey)
      await this.createCheckoutSessionRequest(current.id, plan, quantities)
      try { trackAction(CsmAction.BillingCheckoutStarted) } catch { }
    },
    loadActivePricingPlans: async function (): Promise<void> {
      try {
        const usersStore = useUsersStore()
        if (!usersStore.user) {
          this.pricingPlans = null
          this.pricingPlansError = null
          return
        }
        this.isLoadingPricingPlans = true
        this.pricingPlansError = null
        const db = useFirestore()
        const q = query(
          collection(db, 'pricing_plans'),
          where('active', '==', true),
          limit(1),
        )
        const snap = await getDocs(q)
        const doc = snap.docs[0]
        if (!doc) {
          this.pricingPlans = null
          this.isLoadingPricingPlans = false
          return
        }
        const plans = PricingPlans.fromFirebase(doc)
        this.pricingPlans = plans
        this.isLoadingPricingPlans = false
      } catch (error) {
        logger.error('Error loading pricing plans', error)
        this.pricingPlansError = 'pricing_plans_load_error'
        try { useAppStore().toast('Pricing', 'Error loading pricing plans', 'error', 5000) } catch { }
        this.isLoadingPricingPlans = false
      }
    },
    listInvoicesForCurrentTenant: async function (): Promise<void> {
      try {
        const tenantsStore = useTenantsStore()
        const tenant = tenantsStore.currentTenant
        const subId = tenant?.subscription_data?.subscription_id
        if (!tenant?.id || !subId) {
          this.invoices = []
          return
        }
        const col = collection(useFirestore(), 'tenants', tenant.id, 'subscriptions', subId, 'invoices')
        // Order by the raw numeric field in Firestore (seconds) and limit for performance
        const q = query(col, orderBy('effective_at', 'desc'), limit(50))
        const snap = await getDocs(q)
        this.invoices = snap.docs
          .map((d) => Invoice.fromFirebase(d))
          .filter((i): i is Invoice => i !== null)
      } catch (error) {
        logger.error('Error loading invoices for current tenant', error)
        try { useAppStore().toast('Invoices', 'Error loading invoices', 'error', 5000) } catch { }
        this.invoices = []
      }
    },
    // (legacy on-demand loader removed)
    createTenantCreationRequest: async function (name: string, plan: TenantPlan, locale: PreferredLanguage): Promise<void> {
      if (this.tenantCreationRequestsUnsub) {
        this.tenantCreationRequestsUnsub()
        this.tenantCreationRequestsUnsub = null
      }
      const usersStore = useUsersStore()
      const firestore = useFirestore()
      const ref = collection(firestore, `users/${usersStore.uid}/tenant_creation_requests/`)
      const payload = {
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        status: "requested",
        name: name,
        plan: plan,
        locale: locale,
      }
      if (useAppStore().debug) logger.debug("createTenantCreationRequest", payload)
      const docref = await addDoc(ref, payload)
      this.tenantCreationRequestsUnsub = onSnapshot(docref, (s) => {
        this.tenantCreationRequestSnapshot = s
      })
    },
    createProjectCreationRequest: async function (
      tenantId: string,
      name: string,
      type: ProjectCreationRequestType,
      admins: string[],
      members: string[],
      company_name: string,
      parent_qms_project_id?: string,
      td_regulation?: string,
      market_placement_date?: Date | null,
    ): Promise<void> {
      if (this.projectCreationRequestsUnsub) {
        this.projectCreationRequestsUnsub()
        this.projectCreationRequestsUnsub = null
      }
      // MYAC-1571 — forget the previous request before writing this one. A caller
      // watching `projectCreationRequest` would otherwise read the last creation's
      // DONE as this one's, and a refused write never replaces it.
      this.projectCreationRequestSnapshot = null
      // Make sure all admins are also members
      members.push(...admins)
      members = [...new Set(members)]
      const firestore = useFirestore()
      const ref = collection(firestore, `tenants/${tenantId}/project_creation_requests`)
      const payload: Record<string, unknown> = {
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        status: "requested",
        name: name,
        type: type,
        admins: admins,
        members: members,
        company_name: company_name,
        ...(parent_qms_project_id && { parent_qms_project_id }),
        ...(td_regulation && { td_regulation }),
        ...(type === ProjectCreationRequestType.TD && { product_name: name }),
        ...(market_placement_date && { market_placement_date: Timestamp.fromDate(market_placement_date) }),
      }
      logger.debug("createProjectCreationRequest", payload)
      const docref = await addDoc(ref, payload)
      try { trackAction(CsmAction.ProjectCreated) } catch { }
      this.projectCreationRequestsUnsub = onSnapshot(docref, (s) => {
        this.projectCreationRequestSnapshot = s
      })
    },
    createCheckoutSessionRequest: async function (tenantId: string, plan: TenantPlan, quantities: Record<string, number>): Promise<void> {
      try {
        if (this.checkoutSessionRequestsUnsub) {
          this.checkoutSessionRequestsUnsub()
          this.checkoutSessionRequestsUnsub = null
        }
        if (useAppStore().debug) logger.info("quantities", quantities)

        // Convert quantities to items array
        const items = Object.entries(quantities).map(([priceId, quantity]) => ({
          price_id: priceId,
          quantity: quantity,
        }))

        if (useAppStore().debug) logger.info("items", items)

        const payload = {
          status: CheckoutSessionRequestStatus.REQUESTED,
          plan: plan,
          items: items,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }

        if (useAppStore().debug) logger.info("createCheckoutSessionRequest", payload)
        const doc = await addDoc(collection(useFirestore(), `tenants/${tenantId}/checkout_session_requests`), payload)

        try { useAnalyticsStore().trackEvent('checkout_session_created', { plan }) } catch { }

        this.checkoutSessionRequestsUnsub = onSnapshot(doc, (s) => {
          this.checkoutSessionRequestSnapshot = s
          // When backend sets the checkout_session reference, subscribe to it
          try {
            const req = CheckoutSessionRequest.fromFirebase(s)
            const db = useFirestore()
            const nextRef = req?.checkout_session?.toDocumentReference(db) || null

            if (nextRef) {
              if (this.checkoutSessionsUnsub) {
                this.checkoutSessionsUnsub()
                this.checkoutSessionsUnsub = null
              }
              this.checkoutSessionsUnsub = onSnapshot(nextRef, (cs) => {
                this.checkoutSessionsSnapshot = cs
              })
            } else {
              // No checkout session reference yet; ensure we don't keep stale data
              if (this.checkoutSessionsUnsub) {
                this.checkoutSessionsUnsub()
                this.checkoutSessionsUnsub = null
              }
              this.checkoutSessionsSnapshot = null
            }
          } catch (e) {
            logger.error('Error handling checkout_session subscription', e)
          }
        })
      } catch (error) {
        try { useAnalyticsStore().trackEvent('checkout_session_request_error', { message: (error as Error)?.message }) } catch { }
        throw error
      }
    },
    getCustomerPortalUrl: async function (): Promise<string | null> {
      try {
        const tenantsStore = useTenantsStore()
        const tenant = tenantsStore.currentTenant
        const subId = tenant?.subscription_data?.subscription_id
        if (!tenant?.id || !subId) {
          useAppStore().toast('Billing', 'No active subscription', 'warn', 4000)
          return null
        }

        // If we already have a customer portal URL, return it
        if (tenant.customer_portal_url) {
          return tenant.customer_portal_url
        }

        // Trigger the customer portal URL update
        await updateModelDoc(tenant, {
          updated_at: serverTimestamp(),
          trigger_customer_portal_update: TenantCustomerPortalUpdateStatus.REQUESTED,
        })

        useAppStore().toast('Billing', 'Customer portal URL requested', 'info', 4000)
        try { useAnalyticsStore().trackEvent('customer_portal_requested') } catch { }
        return null
      } catch (error) {
        logger.error('Error requesting customer portal URL', error)
        useAppStore().toast('Billing', 'Error requesting customer portal', 'error', 4000)
        try { useAnalyticsStore().trackEvent('customer_portal_request_error', { message: (error as Error)?.message }) } catch { }
        return null
      }
    },
    requestCustomerPortalUrlRefresh: async function (): Promise<string | null> {
      try {
        const tenantsStore = useTenantsStore()
        const tenant = tenantsStore.currentTenant
        const subId = tenant?.subscription_data?.subscription_id
        if (!tenant || !tenant?.id || !subId) {
          useAppStore().toast('Billing', 'No active subscription', 'warn', 4000)
          return Promise.resolve(null)
        }
        const tenantRef = tenant.toDocumentReference()
        if (!tenantRef) {
          useAppStore().toast('Billing', 'Error requesting customer portal', 'error', 4000)
          return Promise.resolve(null)
        }

        return new Promise(async (resolve, reject) => {

          // subscribe to tenant updates
          let lastTriggerStatus = TenantCustomerPortalUpdateStatus.REQUESTED
          const tenantUnsub = onSnapshot(tenantRef, (snap) => {
            try {
              const updatedTenant = Tenant.fromFirebase(snap)
              if (!updatedTenant?.trigger_customer_portal_update) {
                return
              }
              if (updatedTenant.trigger_customer_portal_update == TenantCustomerPortalUpdateStatus.FAILED) {
                tenantUnsub()
                return reject(new Error('Error requesting customer portal'))
              }
              if (lastTriggerStatus == TenantCustomerPortalUpdateStatus.PENDING && updatedTenant.trigger_customer_portal_update == TenantCustomerPortalUpdateStatus.DONE) {
                tenantUnsub()
                return resolve(updatedTenant.customer_portal_url)
              }
              lastTriggerStatus = updatedTenant.trigger_customer_portal_update
            } catch (e) {
              tenantUnsub()
              return reject(e)
            }
          })

          await updateModelDoc(tenant, {
            updated_at: serverTimestamp(),
            trigger_customer_portal_update: TenantCustomerPortalUpdateStatus.REQUESTED,
          })
          try { useAnalyticsStore().trackEvent('customer_portal_requested') } catch { }
        })
      } catch (error) {
        logger.error('Error requesting customer portal URL refresh', error)
        useAppStore().toast('Billing', 'Error requesting customer portal', 'error', 4000)
        try { useAnalyticsStore().trackEvent('customer_portal_request_error', { message: (error as Error)?.message }) } catch { }
        return null
      }
    },

    addProjectMember: async function (projectId: string, uid: string, role: 'admin' | 'member'): Promise<void> {
      try {
        if (!projectId || !uid || !role) {
          logger.error('addProjectMember: projectId, uid, and role are required')
          throw new Error('Missing required parameters')
        }
        const db = useFirestore()
        const ref = collection(db, `projects/${projectId}/member_management_requests`)
        const payload = {
          action: MemberManagementAction.ENABLE,
          uid: uid,
          role: role,
          status: MemberManagementRequestStatus.REQUESTED,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }
        logger.debug('addProjectMember: creating request', payload)
        await addDoc(ref, payload)
        try { trackAction(CsmAction.ProjectMemberAdded, { projectId }) } catch { }
      } catch (error) {
        logger.error('Error adding project member', error)
        throw error
      }
    },

    /**
     * MYAC-1175 — a tenant admin joins one of their company's projects on their own
     * initiative, from the admin project list, with no invitation step in between.
     *
     * Joins as `admin`, not `member`: a tenant admin who lands in a project as a plain
     * member still cannot administer it, which is the point of the action.
     *
     * Guarded here for UX only — `firestore.rules` re-checks the same thing
     * (`isTenantAdminOfProject`) on the request doc, and that is the real boundary.
     *
     * Idempotent: no-ops when the caller already holds project admin. The backend is
     * idempotent too (ArrayUnion + a `ProjectMember` doc keyed by uid), so a racing
     * duplicate request cannot produce a duplicate membership either.
     *
     * csm-event: emitted by `addProjectMember` below — this is the same membership
     * gesture reached from the admin list, not a second countable one.
     */
    joinProjectAsAdmin: async function (projectId: string): Promise<void> {
      if (!projectId) {
        logger.error('joinProjectAsAdmin: projectId is required')
        throw new Error('Missing required parameters')
      }
      const uid = useUsersStore().uid
      if (!uid) {
        logger.error('joinProjectAsAdmin: no authenticated user')
        throw new Error('No authenticated user')
      }

      const db = useFirestore()
      // Mapped through the model rather than read off the raw snapshot, per
      // the `front-data` skill — `fromFirebase` also returns null for a missing
      // document, which is the not-found branch below.
      const project = Project.fromFirebase(await getDoc(doc(db, 'projects', projectId)))
      if (project === null) {
        logger.error('joinProjectAsAdmin: project not found', { projectId })
        throw new Error('Project not found')
      }

      const projectTenantId = project.tenant?.id
      const currentTenant = useTenantsStore().currentTenant
      if (!currentTenant || !projectTenantId || currentTenant.id !== projectTenantId) {
        logger.error('joinProjectAsAdmin: project does not belong to the current company', { projectId, projectTenantId })
        throw new Error('Project does not belong to the current tenant')
      }
      if (!currentTenant.admins?.includes(uid)) {
        logger.error('joinProjectAsAdmin: user is not a tenant admin', { projectId, uid })
        throw new Error('User is not a tenant admin')
      }

      const members = project.members ?? []
      const admins = project.admins ?? []
      if (members.includes(uid) && admins.includes(uid)) {
        logger.debug('joinProjectAsAdmin: already a project admin, nothing to request', { projectId, uid })
        return
      }

      await this.addProjectMember(projectId, uid, 'admin')
    },

    updateProjectMemberRole: async function (projectId: string, uid: string, role: 'admin' | 'member'): Promise<void> {
      try {
        if (!projectId || !uid || !role) {
          logger.error('updateProjectMemberRole: projectId, uid, and role are required')
          throw new Error('Missing required parameters')
        }
        const db = useFirestore()
        const ref = collection(db, `projects/${projectId}/member_management_requests`)
        const payload = {
          action: MemberManagementAction.UPDATE,
          uid: uid,
          role: role,
          status: MemberManagementRequestStatus.REQUESTED,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }
        logger.debug('updateProjectMemberRole: creating request', payload)
        await addDoc(ref, payload)
        try { trackAction(CsmAction.ProjectMemberRoleChanged, { projectId, projectMeta: { role } }) } catch { }
      } catch (error) {
        logger.error('Error updating project member role', error)
        throw error
      }
    },

    updateTenantMemberRole: async function (tenantId: string, uid: string, role: 'admin' | 'member'): Promise<void> {
      try {
        if (!tenantId || !uid || !role) {
          logger.error('updateTenantMemberRole: tenantId, uid, and role are required')
          throw new Error('Missing required parameters')
        }
        const tenantsStore = useTenantsStore()
        const currentTenant = tenantsStore.currentTenant
        const usersStore = useUsersStore()
        const currentUserId = usersStore.uid

        if (!currentTenant) {
          logger.error('updateTenantMemberRole: no current tenant')
          throw new Error('No current tenant')
        }

        if (!currentTenant.admins.includes(currentUserId)) {
          logger.error('updateTenantMemberRole: user is not a tenant admin', { currentUserId, tenantAdmins: currentTenant.admins })
          throw new Error('User is not a tenant admin')
        }

        const db = useFirestore()
        const ref = collection(db, `tenants/${tenantId}/member_management_requests`)
        const payload = {
          action: MemberManagementAction.UPDATE,
          uid: uid,
          role: role,
          status: MemberManagementRequestStatus.REQUESTED,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }
        logger.debug('updateTenantMemberRole: creating request', { tenantId, payload, currentUserId, isAdmin: currentTenant.admins.includes(currentUserId) })
        await addDoc(ref, payload)
      } catch (error) {
        logger.error('Error updating tenant member role', error)
        throw error
      }
    },

    removeProjectMember: async function (projectId: string, uid: string): Promise<void> {
      try {
        if (!projectId || !uid) {
          logger.error('removeProjectMember: projectId and uid are required')
          throw new Error('Missing required parameters')
        }
        const db = useFirestore()
        const ref = collection(db, `projects/${projectId}/member_management_requests`)
        const payload = {
          action: MemberManagementAction.DISABLE,
          uid: uid,
          role: null,
          status: MemberManagementRequestStatus.REQUESTED,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }
        logger.debug('removeProjectMember: creating request', payload)
        await addDoc(ref, payload)
      } catch (error) {
        logger.error('Error removing project member', error)
        throw error
      }
    },

    removeTenantMember: async function (tenantId: string, uid: string): Promise<void> {
      try {
        if (!tenantId || !uid) {
          logger.error('removeTenantMember: tenantId and uid are required')
          throw new Error('Missing required parameters')
        }
        const db = useFirestore()
        const ref = collection(db, `tenants/${tenantId}/member_management_requests`)
        const payload = {
          action: MemberManagementAction.DISABLE,
          uid: uid,
          role: null,
          status: MemberManagementRequestStatus.REQUESTED,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        }
        logger.debug('removeTenantMember: creating request', payload)
        await addDoc(ref, payload)
        try { trackAction(CsmAction.TeamMemberRemoved) } catch { }
      } catch (error) {
        logger.error('Error removing tenant member', error)
        throw error
      }
    }
  }
})
